<script setup lang="ts" generic="Value extends MultiselectDropdownMenuValue = MultiselectDropdownMenuValue">
import { type Component, computed, nextTick, reactive, ref, useSlots } from "vue";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";

import Animated from "~/components/dumb/Animated.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import Template from "~/components/dumb/Template.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { MAX_ITEMS_PER_SECTION, ROW_TYPE } from "~/constants/dropdown";
import { AlertIcon, PlusCircleIcon, XIcon } from "~/icons";
import { Placement } from "~/shared/enums";
import type {
  MultiselectDropdownMenuItem,
  MultiselectDropdownMenuSection,
  MultiselectDropdownMenuValue,
  ResolvedNoOptionAction,
} from "~/shared/types";
import { usePageStore } from "~/stores";
import { makeUuid, pickComponentAttrs } from "~/utils/common";
import useLastInputType from "~/utils/useLastInputType";

const props = defineProps<{
  filteredSections: MultiselectDropdownMenuSection<Value>[];
  items: MultiselectDropdownMenuItem<Value>[];
  search: string | undefined;
  placeholder: string | undefined;
  emptyText: string | undefined;
  itemClasses: string[] | undefined;
  enableNewEntry: boolean | undefined;
  newEntryIsValid: boolean | undefined;
  newEntryError: string | undefined;
  newEntryAllowed: boolean;
  newEntryIcon: Component | undefined;
  newEntryIconArgs: object | undefined;
  makeNewEntryText: ((search: string) => string) | undefined;
  hasNewEntrySlot: boolean | undefined;
  noOptionActions: ResolvedNoOptionAction[];
  infiniteList: boolean | undefined;
  loadingMore: boolean | undefined;
  block: boolean | undefined;
  autoSize: boolean | undefined;
  widthPixels: number | undefined;
  minItemSize: number | undefined;
  colors: Record<string, string> | undefined;
  showAll: boolean | undefined;
  showMoreForSingleSection: boolean | undefined;
}>();

const emit = defineEmits<{
  select: [item: MultiselectDropdownMenuItem<Value>];
  remove: [value: Value];
  create: [];
  replace: [value: Value];
  updateSearch: [value: string];
  keydown: [event: KeyboardEvent];
  backspace: [event: KeyboardEvent];
}>();

const slots = useSlots();

const pageStore = usePageStore();

const calcWidthPx = computed(() => props.widthPixels ?? 224);

const internalSearch = ref("");

const searchInput = ref<HTMLInputElement | null>(null);
const inputId = ref(`multiselect-search-${makeUuid()}`);
const menu = ref<{ $el: HTMLElement } | null>(null);
const scrollContainer = computed(() => menu.value?.$el ?? null);

const { isKeyboard: isNavigatingWithKeyboard } = useLastInputType();
const indexOfFocusedItem = ref(-1);

const itemRefs = reactive(new Map<number, HTMLElement>());
const assignItemRef = (index: number, e: { $el?: HTMLElement } | null) => {
  if (e?.$el && e.$el instanceof HTMLElement) {
    itemRefs.set(index, e.$el);
  }
};

const isMobile = computed(() => pageStore.isMobile);
const selectedItems = computed(() => {
  const seen = new Set();
  return props.items.filter((item) => {
    if (!item.selected || seen.has(item.value)) {
      return false;
    }
    seen.add(item.value);
    return true;
  });
});
const allItemsSelected = computed(() => props.items.every((item) => item.selected));

const search = computed(() => props.search ?? internalSearch.value);

const visibleNoOptionActions = computed(() => props.noOptionActions.filter((action) => action.allowed));
const hasNoMatch = computed(
  () => search.value.length > 0 && props.filteredSections.every((section) => section.items.length === 0)
);
const isShowingActions = computed(() => hasNoMatch.value && visibleNoOptionActions.value.length > 0);

const expandedSections = ref<Set<string>>(new Set());

const toggleSection = (title: string) => {
  if (expandedSections.value.has(title)) {
    expandedSections.value.delete(title);
  } else {
    expandedSections.value.add(title);
    nextTick(() => {
      const section = props.filteredSections.find((e) => e.title === title);
      const firstNewItem = section?.items[MAX_ITEMS_PER_SECTION];
      if (firstNewItem) {
        const targetElement = menu.value?.$el?.querySelector(
          `[data-item-value="${firstNewItem.value}"][tabindex="0"]`
        ) as HTMLElement;
        targetElement?.focus();
      }
    });
  }
};

const rows = computed(() => {
  const res: {
    id: string;
    type: string;
    section: MultiselectDropdownMenuSection<Value>;
    item?: MultiselectDropdownMenuItem<Value>;
    action?: ResolvedNoOptionAction;
  }[] = [];

  const populatedSections = props.filteredSections.filter((e) => e.items.length > 0);
  const hasMultipleSections = populatedSections.length > 1;
  const lastPopulatedSection = populatedSections.at(-1);

  props.filteredSections.forEach((section, index) => {
    if (section.items.length === 0) {
      return;
    }

    const isExpanded = expandedSections.value.has(section.title);
    const hasEnoughHiddenItems =
      (hasMultipleSections || props.showMoreForSingleSection) && section.items.length > MAX_ITEMS_PER_SECTION + 1;
    const visibleItems =
      hasEnoughHiddenItems && !isExpanded && !props.showAll
        ? section.items.slice(0, MAX_ITEMS_PER_SECTION)
        : section.items;

    res.push({ id: `section-${section.title}-start`, type: ROW_TYPE.SECTION_START, section });
    visibleItems.forEach((item) => {
      const id = item.customKey || `item-${item.value}-${item.label}`;
      res.push({ id, type: ROW_TYPE.ITEM, item, section });
    });

    if (hasEnoughHiddenItems && !isExpanded && !props.showAll) {
      res.push({ id: `section-${index}-show-more`, type: ROW_TYPE.SHOW_MORE, section });
    }

    if (section !== lastPopulatedSection) {
      res.push({ id: `section-${section.title}-end`, type: ROW_TYPE.SECTION_END, section });
    }
  });

  if (isShowingActions.value) {
    visibleNoOptionActions.value.forEach((action, index) => {
      res.push({ id: `action-${index}`, type: ROW_TYPE.ACTION, section: { title: "", items: [] }, action });
    });
  }

  return res.map((e, rowIndex) => ({ ...e, rowIndex }));
});

const isFocusableRow = (e: {
  type: string;
  item?: MultiselectDropdownMenuItem<Value>;
  action?: ResolvedNoOptionAction;
}) =>
  (e.type === ROW_TYPE.ITEM && e.item && !e.item.disabled) ||
  e.type === ROW_TYPE.SHOW_MORE ||
  (e.type === ROW_TYPE.ACTION && !!e.action?.isValid);

const setFocusedIndex = (index: number) => {
  if (isNavigatingWithKeyboard.value) {
    return;
  }
  indexOfFocusedItem.value = index;
};

const clearFocusedIndex = () => {
  indexOfFocusedItem.value = -1;
};

const clearFocus = () => {
  if (isNavigatingWithKeyboard.value) {
    return;
  }
  clearFocusedIndex();
};

const onBackspace = (event: KeyboardEvent) => {
  if (search.value !== "") {
    return;
  }
  emit("backspace", event);
};

const selectAdjacentItem = (forward: boolean) => {
  const focusableRows = rows.value.filter(isFocusableRow);

  if (focusableRows.length === 0) {
    return;
  }

  const items = forward ? focusableRows : [...focusableRows].reverse();
  const currentIndex = indexOfFocusedItem.value;

  const nextRow = items.find((e) => {
    const rowIndex = rows.value.indexOf(e);
    return forward ? rowIndex > currentIndex : rowIndex < currentIndex;
  });

  if (!nextRow) {
    if (!forward && currentIndex > 0) {
      indexOfFocusedItem.value = -1;
      nextTick(() => {
        searchInput.value?.focus();
        searchInput.value?.scrollIntoView({ block: "nearest" });
      });
    }
    return;
  }

  const newIndex = rows.value.indexOf(nextRow);
  indexOfFocusedItem.value = newIndex;

  nextTick(() => {
    const el = itemRefs.get(newIndex);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  });
};

const selectNextItem = () => {
  selectAdjacentItem(true);
};
const selectPreviousItem = () => {
  selectAdjacentItem(false);
};
const selectFirstItem = () => {
  indexOfFocusedItem.value = rows.value.findIndex(isFocusableRow);
};

const focusedItem = computed(() => {
  const row = rows.value[indexOfFocusedItem.value];
  return row?.type === ROW_TYPE.ITEM ? row.item : null;
});

const focusedAction = computed(() => {
  const row = rows.value[indexOfFocusedItem.value];
  return row?.type === ROW_TYPE.ACTION ? row.action : null;
});

const onSelectItem = (item: MultiselectDropdownMenuItem<Value>) => {
  emit("select", item);
};

const clearSearch = () => {
  internalSearch.value = "";
  emit("updateSearch", "");
};

const getItemBindings = (item: MultiselectDropdownMenuItem<Value>, extraAttrs: Record<string, unknown> = {}) => ({
  ...(item.componentArgs ?? {}),
  ...pickComponentAttrs(item.component, {
    label: item.label,
    selected: item.selected,
    disabled: item.disabled,
    ...extraAttrs,
  }),
});

defineExpose({
  scrollContainer,
  clearExpandedSections: () => expandedSections.value.clear(),
  clearFocus: clearFocusedIndex,
  clearSearch,
  focusSearch: () => searchInput.value?.focus(),
  selectPreviousItem,
  selectNextItem,
  selectFirstItem,
  focusedItem,
  focusedAction,
  isShowingActions,
});
</script>

<template>
  <DynamicScroller
    ref="menu"
    class="overflow-y-auto"
    :class="[
      block && !pageStore.isMobile && 'w-full min-w-[224px]',
      !pageStore.isMobile ? 'max-h-96 rounded-t border border-md bg-lt focus-ring-none app-drag-none' : 'size-full',
    ]"
    :items="rows"
    :min-item-size="minItemSize ?? 1"
    key-field="id"
    :style="{
      '--background': colors?.borderVlt,
      '--highlight': colors?.borderMd,
      ...(autoSize
        ? {}
        : {
            width: `${calcWidthPx}px`,
            'max-width': `${calcWidthPx}px`,
          }),
    }"
    @keydown.stop="emit('keydown', $event)"
    @mouseleave="clearFocus"
    @focusout="clearFocus">
    <template #before>
      <Animated
        v-if="selectedItems.length && !isMobile"
        class="flex cursor-text flex-wrap items-center gap-1 px-3 pt-3"
        @click="searchInput?.focus()"
        @keydown.enter="searchInput?.focus()">
        <div v-for="item in selectedItems" :key="item.value?.toString()" class="cursor-default overflow-hidden">
          <component
            :is="item.component"
            v-bind="getItemBindings(item, { isChip: true, onRemove: () => emit('remove', item.value) })" />
        </div>
      </Animated>

      <form v-if="!isMobile" class="relative mb-1 flex items-center" autocomplete="off" @submit.prevent>
        <label :for="inputId" class="sr-only">Search</label>
        <input
          :id="inputId"
          ref="searchInput"
          v-model="internalSearch"
          type="text"
          class="block w-full border-x-0 border-t-0 border-b border-md bg-transparent text-sm text-hvy focus-ring-none focus-visible-ring-md placeholder:text-vlt focus-visible:border-md"
          :class="[items.length === selectedItems.length && 'border-none', internalSearch && 'pr-7']"
          :placeholder="placeholder"
          @mousedown.stop
          @input="emit('updateSearch', internalSearch)"
          @keydown.backspace="onBackspace"
          @keydown.delete="onBackspace" />
        <button
          v-if="internalSearch"
          type="button"
          class="absolute right-2 flex cursor-pointer items-center justify-center rounded-sm hover-bg-quiet p-0.5 text-lt"
          @click.stop="clearSearch"
          @mousedown.prevent>
          <XIcon class="icon-xs" />
        </button>
      </form>
    </template>

    <template #default="{ item: { id, section, item, type, rowIndex, action }, index, active }">
      <!-- Section start and end -->
      <DynamicScrollerItem
        v-if="type === ROW_TYPE.SECTION_START || type === ROW_TYPE.SECTION_END"
        :item="{ id }"
        :active="active"
        class="w-full truncate border-md text-vlt"
        :class="{
          'border-b pb-1': type === ROW_TYPE.SECTION_END && index < rows.length - 1,
          'pt-1': type === ROW_TYPE.SECTION_START && section.showTitle !== false,
        }">
        <span
          v-if="section.showTitle && type === ROW_TYPE.SECTION_START"
          class="text-xs/3 font-semibold uppercase select-none"
          :class="isMobile ? 'px-5' : 'px-3'">
          {{ section.title }}
        </span>
      </DynamicScrollerItem>

      <!-- Show more -->
      <DynamicScrollerItem
        v-else-if="type === ROW_TYPE.SHOW_MORE && !showAll"
        :ref="(e: any) => assignItemRef(rowIndex, e)"
        :item="{ id }"
        :active="active"
        :tabindex="0"
        :class="['w-full cursor-pointer focus-ring-none', !isMobile && indexOfFocusedItem === rowIndex && 'bg-md']"
        @keydown.enter.stop="toggleSection(section.title)"
        @mouseover="setFocusedIndex(rowIndex)"
        @focusin="setFocusedIndex(rowIndex)">
        <button
          type="button"
          class="flex w-full items-center justify-center px-3 py-1.5 text-xs font-medium text-vlt"
          tabindex="-1"
          @click.prevent.stop="toggleSection(section.title)"
          @mousedown.prevent>
          Show more
        </button>
      </DynamicScrollerItem>

      <!-- Item -->
      <DynamicScrollerItem
        v-else-if="item"
        :ref="(e: any) => assignItemRef(rowIndex, e)"
        :item="{ id }"
        :active="active"
        :tabindex="0"
        :disabled="item.disabled"
        :data-item-value="item.value"
        class="flex focus-ring-none data-[focused]:bg-md"
        :class="[...(itemClasses ?? []), item.disabled ? 'cursor-default' : 'cursor-pointer']"
        :data-focused="!isMobile && indexOfFocusedItem === rowIndex ? '' : undefined"
        @click.prevent="onSelectItem(item)"
        @keydown.enter.prevent.stop="onSelectItem(item)"
        @mouseover="setFocusedIndex(rowIndex)"
        @focusin="setFocusedIndex(rowIndex)">
        <component
          :is="item.disabled && item.disabledReason ? Tooltip : Template"
          :placement="Placement.LEFT"
          :text="item.disabledReason"
          block
          :show-delay="200">
          <div v-if="item.disabled" class="absolute inset-0" />
          <component
            :is="item.component"
            v-bind="getItemBindings(item, { onSelect: onSelectItem, onReplace: () => emit('replace', item.value) })" />
        </component>
      </DynamicScrollerItem>

      <!-- No-option action -->
      <DynamicScrollerItem
        v-else-if="type === ROW_TYPE.ACTION && action"
        :ref="(e: any) => assignItemRef(rowIndex, e)"
        :item="{ id }"
        :active="active"
        :tabindex="action.isValid ? 0 : -1"
        class="w-full focus-ring-none"
        :class="[
          action.isValid ? 'cursor-pointer' : 'cursor-default',
          !isMobile && action.isValid && indexOfFocusedItem === rowIndex && 'bg-md',
        ]"
        @click.prevent.stop="action.isValid && action.onAction()"
        @keydown.enter.prevent.stop="action.isValid && action.onAction()"
        @mouseover="action.isValid && setFocusedIndex(rowIndex)"
        @focusin="action.isValid && setFocusedIndex(rowIndex)">
        <Tooltip :disabled="action.isValid" :text="action.error" block :placement="Placement.TOP">
          <div
            class="flex w-full items-center gap-2 text-sm select-none"
            :class="[action.isValid ? 'text-md' : 'text-vlt', isMobile ? 'py-2.5 pr-4 pl-5' : 'py-1 pr-2 pl-3']">
            <component
              :is="action.icon ?? PlusCircleIcon"
              v-bind="action.iconArgs"
              class="icon-sm text-vlt"
              :class="!action.isValid && 'opacity-50'" />
            <span class="break-words hyphens-auto">{{ action.text }}</span>
          </div>
        </Tooltip>
      </DynamicScrollerItem>
    </template>

    <template #after>
      <Tooltip
        v-if="search.length > 0 && enableNewEntry && newEntryAllowed"
        :disabled="newEntryIsValid"
        :text="newEntryError"
        block
        :placement="Placement.TOP">
        <div
          tabindex="0"
          class="flex w-full items-center gap-2 text-sm select-none"
          :class="[
            newEntryIsValid ? 'cursor-pointer text-md focus-ring-none' : 'cursor-default text-vlt',
            isMobile ? 'py-2.5 pr-4 pl-5' : 'py-1 pr-2 pl-3',
            !isMobile && newEntryIsValid && 'hover:bg-md focus-visible:bg-md',
          ]"
          @click.prevent="emit('create')"
          @keydown.enter.prevent.stop="emit('create')">
          <component
            v-bind="newEntryIconArgs"
            :is="hasNewEntrySlot ? null : (newEntryIcon ?? PlusCircleIcon)"
            class="icon-sm text-vlt"
            :class="!newEntryIsValid && 'opacity-50'" />
          <span v-if="!hasNewEntrySlot" class="break-words hyphens-auto">
            {{ makeNewEntryText?.(search) ?? `Create ${search}` }}
          </span>
          <slot v-else name="newEntrySlot" />
        </div>
      </Tooltip>

      <div
        v-else-if="!isShowingActions && filteredSections.flatMap((section) => section.items).length === 0"
        tabindex="0"
        class="flex cursor-default items-center gap-2 text-sm text-vlt opacity-50 select-none"
        :class="isMobile ? 'py-2.5 pr-4 pl-5' : 'py-1 pr-2 pl-3'">
        <component
          :is="
            search.length > 0 || (allItemsSelected && !enableNewEntry) || !newEntryAllowed ? AlertIcon : PlusCircleIcon
          "
          class="icon-md text-vlt" />
        <span>
          {{
            (allItemsSelected && !enableNewEntry) || !newEntryAllowed
              ? (emptyText ?? `No${items.length > 0 ? " more" : ""} options`)
              : search.length > 0
                ? "No results, search again"
                : "Type to add an option"
          }}
        </span>
      </div>
      <div v-if="!isShowingActions && slots.extra" @mouseenter="clearFocusedIndex" @focusin="clearFocusedIndex">
        <slot name="extra" />
      </div>
      <LoadingSpinner v-if="infiniteList && loadingMore" :class="isMobile ? 'py-3' : 'py-1.5'" small />
    </template>
  </DynamicScroller>
</template>
